/*
  Радар взыскания — §3.9.

  🔴 Экран переделан 22.09.2026 по решению владельца: вместо девяти одинаковых
  карточек - две плитки с суммами, переключатель срочности и одна таблица.
  Конституция §3.9.4 таблицу запрещала; раздел правлен тем же решением, и
  причина записана там, а не только здесь.

  Экран показывает поводы вернуться к делам, которые считались законченными, и
  **вместе с ними** - чего радар не видит. Второе не менее важно первого:
  молчание о неработающей находке неотличимо от её отсутствия, и заказчик решил
  бы, что проверено всё.

  Порядок находок задаёт сервер: срочность важнее суммы, потому что горящие
  деньги исчезнут сами, а остальные никуда не денутся. Здесь список только
  ОТБИРАЕТСЯ по срочности - порядок внутри отбора остаётся серверным.
  Пересортировать его на клиенте нельзя: это был бы второй ответ на вопрос
  «с чего начать».
*/

import { useState } from 'react'

import { Empty, Switch } from '../components/Common.jsx'
import RadarFindings from '../components/RadarFindings.jsx'
import { date, money, plural } from '../api/format.js'

const ALL = 'all'
const BURNING = 'burning'
const OPPORTUNITY = 'opportunity'

const findingsWord = (count) => plural(count, 'находка', 'находки', 'находок')

/*
  С 21.10.2026 страницы находок и отбор по срочности делает сервер (требование владельца:
  «пагинация серверная»). Сводка - суммы плиток, числа в подписях переключателя, перечень
  «чего радар не видит» - приходит вместе с первой страницей и считается по ВСЕМ находкам:
  по одной странице «Горит 2» было бы неверным числом.
*/
export default function RadarPage() {
  const [report, setReport] = useState(null)
  const [urgency, setUrgency] = useState(ALL)

  const burning = report?.burning_count ?? 0
  const opportunity = report?.opportunity_count ?? 0
  const none = report !== null && burning + opportunity === 0

  return (
    <>
      <div className="head">
        <div className="head__title">Радар взыскания</div>
        {report ? (
          <div className="head__note">
            на {date(report.as_of)} · находок на {money(report.total)}
          </div>
        ) : null}
      </div>

      {none ? (
        <Empty title="Радар ничего не нашёл">
          По портфелю нет ни горящих сроков, ни неиспользованных возможностей. Это
          нормальное состояние, а не ошибка: радар смотрит заново при каждом открытии.
        </Empty>
      ) : (
        <>
          {report ? <Totals report={report} burning={burning} opportunity={opportunity} /> : null}
          {/*
            Переключатель отбирает, а не сортирует. Счётчик стоит в подписи:
            «Горит 2» отвечает на вопрос до нажатия, а пустая полоса после
            нажатия читалась бы как поломка.
          */}
          {report ? (
            <div className="row">
              <Switch
                value={urgency}
                onChange={setUrgency}
                options={[
                  { value: ALL, title: `Всё ${burning + opportunity}` },
                  { value: BURNING, title: `Горит ${burning}` },
                  { value: OPPORTUNITY, title: `Возможность ${opportunity}` },
                ]}
              />
            </div>
          ) : null}

          <div className="card">
            {/* Переключатель срочности - это другой список, и читать его начинают с первой
                страницы: ключ пересоздаёт список, а с ним и номер страницы. */}
            <RadarFindings key={urgency} urgency={urgency} onReport={setReport} />
          </div>
        </>
      )}

      {report ? (
        <div className="card">
          <div className="section__title">Чего радар пока не видит</div>
          <div className="muted">
            Перечислено намеренно: умолчать о неработающей находке значит дать поверить,
            что проверено всё.
          </div>
          <ul>
            {report.missing_sources.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  )
}

/*
  Две суммы рядом, а не одна строка под заголовком. Горящее и возможности -
  разные деньги: первые исчезнут сами, вторые подождут, и сравнивать их человек
  должен взглядом, а не чтением (§4.5.3). Цвет - тот же, что во всём кабинете:
  красный означает потерю денег во времени и ничего другого.

  Суммы берутся с сервера обе: вычитать одну из другой на клиенте значило бы
  считать деньги в числах с плавающей точкой.
*/
function Totals({ report, burning, opportunity }) {
  return (
    <div className="grid grid--even">
      <div className="tile tile--loss">
        <div className="money__label">Горит</div>
        <div className="money__value money__value--burn">{money(report.burning_total)}</div>
        <div className="money__note">
          {findingsWord(burning)} · исчезнет само, если не сделать ничего
        </div>
      </div>
      <div className="tile tile--plain">
        <div className="money__label">Возможность</div>
        <div className="money__value">{money(report.opportunity_total)}</div>
        <div className="money__note">
          {findingsWord(opportunity)} · вернётся в работу, но не исчезнет
        </div>
      </div>
    </div>
  )
}
